import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Card, CardContent } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Badge } from "../components/ui/badge";
import {
  Plus,
  Search,
  MoreHorizontal,
  Pencil,
  Trash2,
  Download,
  Server,
  Copy,
  Activity,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

export default function SSPListPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [ssps, setSsps] = useState([]);
  const [sspQPS, setSspQPS] = useState({});
  const [qpsSummary, setQpsSummary] = useState({ total: 0, allocated: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchSSPs();
    fetchQPS();
    
    // Auto-refresh QPS every 10 seconds
    const interval = setInterval(fetchQPS, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchSSPs = async () => {
    try {
      const response = await api.get("/ssps");
      setSsps(response.data);
      
      // Calculate QPS summary
      const activeSSPs = response.data.filter(ssp => ssp.status === "active");
      const totalQPS = response.data.reduce((sum, ssp) => sum + (ssp.qps_limit || 0), 0);
      const allocatedQPS = activeSSPs.reduce((sum, ssp) => sum + (ssp.qps_limit || 0), 0);
      setQpsSummary({ total: totalQPS, allocated: allocatedQPS });
    } catch (error) {
      toast.error("Failed to fetch SSPs");
    } finally {
      setLoading(false);
    }
  };

  const fetchQPS = async () => {
    try {
      const response = await api.get("/qps/by-ssp?period=today");
      // Create a map of endpoint_id to QPS data
      const qpsMap = {};
      response.data.forEach(item => {
        qpsMap[item.endpoint_id] = item;
      });
      setSspQPS(qpsMap);
    } catch (error) {
      // Silent fail for QPS
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/ssps/${deleteId}`);
      setSsps(ssps.filter((s) => s.id !== deleteId));
      toast.success("SSP deleted successfully");
    } catch (error) {
      toast.error("Failed to delete SSP");
    } finally {
      setDeleteId(null);
    }
  };

  const handleExport = async (format) => {
    try {
      const response = await api.get(`/export/ssps?format=${format}`, {
        responseType: format === "csv" ? "blob" : "json",
      });
      
      if (format === "csv") {
        const blob = new Blob([response.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "ssps.csv";
        a.click();
      } else {
        const blob = new Blob([JSON.stringify(response.data, null, 2)], {
          type: "application/json",
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "ssps.json";
        a.click();
      }
      toast.success(`SSPs exported as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error("Failed to export SSPs");
    }
  };

  const copyEndpoint = (endpoint) => {
    navigator.clipboard.writeText(endpoint);
    toast.success("Endpoint URL copied to clipboard");
  };

  const toggleStatus = async (ssp) => {
    const newStatus = ssp.status === "active" ? "paused" : "active";
    try {
      await api.put(`/ssps/${ssp.id}`, { status: newStatus });
      setSsps(ssps.map(s => s.id === ssp.id ? { ...s, status: newStatus } : s));
      toast.success(`SSP ${newStatus === "active" ? "activated" : "paused"}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredSSPs = ssps.filter(
    (ssp) =>
      ssp.name.toLowerCase().includes(search.toLowerCase()) ||
      ssp.company_name?.toLowerCase().includes(search.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      active: "bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20",
      inactive: "bg-zinc-500/10 text-zinc-500 hover:bg-zinc-500/20",
      paused: "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20",
    };
    return (
      <Badge className={variants[status] || variants.inactive} variant="outline">
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="ssp-list-page">
      {/* QPS Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total QPS Limit</p>
                <p className="text-3xl font-bold text-blue-600">
                  {qpsSummary.total.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">across all SSP endpoints</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Server className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Allocated QPS</p>
                <p className="text-3xl font-bold text-green-600">
                  {qpsSummary.allocated.toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">active endpoints only</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <Activity className="h-6 w-6 text-green-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Available QPS</p>
                <p className="text-3xl font-bold text-purple-600">
                  {(qpsSummary.total - qpsSummary.allocated).toLocaleString()}
                </p>
                <p className="text-xs text-muted-foreground mt-1">paused endpoints</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center">
                <Zap className="h-6 w-6 text-purple-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Server className="h-8 w-8 text-accent" />
            SSP Endpoints
          </h1>
          <p className="text-muted-foreground">
            Manage your Supply-Side Platform endpoints
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="export-btn">
                <Download className="mr-2 h-4 w-4" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport("json")}>
                Export as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport("csv")}>
                Export as CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Link to="/ssps/new">
            <Button data-testid="create-ssp-btn">
              <Plus className="mr-2 h-4 w-4" />
              Create SSP
            </Button>
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search SSPs..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="search-input"
        />
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
        </div>
      ) : filteredSSPs.length === 0 ? (
        <div className="text-center py-12 border rounded-lg" data-testid="empty-state">
          <Server className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No SSPs found</h3>
          <p className="text-muted-foreground mb-4">
            {search ? "Try a different search term" : "Create your first SSP endpoint"}
          </p>
          {!search && (
            <Link to="/ssps/new">
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Create SSP
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table data-testid="ssp-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Connection</TableHead>
                <TableHead>Region</TableHead>
                <TableHead className="text-right">QPS Limit</TableHead>
                <TableHead className="text-right">In QPS</TableHead>
                <TableHead className="text-right">Valid QPS</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSSPs.map((ssp) => {
                const qps = sspQPS[ssp.id] || {};
                return (
                <TableRow key={ssp.id} data-testid={`ssp-row-${ssp.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{ssp.name}</p>
                      <button
                        onClick={() => copyEndpoint(ssp.endpoint_url)}
                        className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mt-1"
                      >
                        <Copy className="h-3 w-3" />
                        Copy endpoint
                      </button>
                    </div>
                  </TableCell>
                  <TableCell>{ssp.company_name}</TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{ssp.connection_type}</span>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-xs">{ssp.region}</span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-sm font-medium">
                      {ssp.qps_limit ? ssp.qps_limit.toLocaleString() : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-sm text-blue-600">
                      {qps.incoming_qps ? (qps.incoming_qps >= 1 ? qps.incoming_qps.toFixed(0) : qps.incoming_qps.toFixed(2)) : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-mono text-sm text-green-600">
                      {qps.valid_qps ? (qps.valid_qps >= 1 ? qps.valid_qps.toFixed(0) : qps.valid_qps.toFixed(2)) : "-"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={ssp.status === "active"}
                        onCheckedChange={() => toggleStatus(ssp)}
                        data-testid={`ssp-status-toggle-${ssp.id}`}
                      />
                      <span className={`text-xs font-medium ${ssp.status === "active" ? "text-green-600" : "text-orange-500"}`}>
                        {ssp.status === "active" ? "Active" : "Paused"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`ssp-actions-${ssp.id}`}>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/ssps/${ssp.id}/edit`)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => setDeleteId(ssp.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete SSP</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this SSP endpoint? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
